import { useEffect, useRef, useState, useMemo } from 'react';
import logger from '../lib/logger';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { apiGet, apiOrigin } from '../lib/api';
import { slugify, initialsOf } from '../lib/slug';
import { getPublicDisplayName } from '../utils/displayName';
import { MapPin, Star, BadgeDollarSign, ShieldCheck, Tag, Calendar } from 'lucide-react';
import ContactCreativeButton from '../components/ContactCreativeButton';
import { useIcon } from '../lib/icons.js';
import { useCurrency } from '../context/CurrencyContext';
import Skeleton from '../components/Skeleton';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import QuickFikaBooking from './booking/QuickFikaBooking';

export default function CreativeDetail() {
  const { id, slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { formatPrice, convert, currency: userCurrency } = useCurrency();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [skills, setSkills] = useState([]);
  const [services, setServices] = useState([]);
  
  const [loadingExtra, setLoadingExtra] = useState(false);
  const [errorExtra, setErrorExtra] = useState(null);
  // Added: portfolio and reviews state
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [loadingPR, setLoadingPR] = useState(false);
  const [errorPR, setErrorPR] = useState(null);
  // New: map for category id -> name and image error tracking
  const [categoryMap, setCategoryMap] = useState({});
  const [imgErrorById, setImgErrorById] = useState({});
  // Portfolio lightbox (use same library as creative dashboard)
  const [lbOpen, setLbOpen] = useState(false);
  const [lbIndex, setLbIndex] = useState(0);
  const lbSlides = useMemo(() => {
    const items = Array.isArray(portfolioItems) ? portfolioItems : [];
    return items
      .map((p) => p?.image_url || p?.image || p?.cover_image || '')
      .filter(Boolean)
      .map((src) => ({ src: normalizeImageUrl(src) || '/vite.svg' }));
  }, [portfolioItems]);

  const [showBooking, setShowBooking] = useState(false);
  const bookingRef = useRef(null);

  const openLightboxAt = (i) => {
    setLbIndex(Math.max(0, i));
    setLbOpen(true);
  };
  const [candidates, setCandidates] = useState([]);
  
  const lastFetchKeyRef = useRef(null);
  const redirectedRef = useRef(false);

  const routeKey = slug ?? id ?? '';
  const fullUuidRegex = /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
  const match = String(routeKey).match(fullUuidRegex);
  const creativeId = match ? match[1] : id;

  useEffect(() => {
    (async () => {
      try {
        if (!creativeId && !slug) {
          setError('Missing creative identifier');
          setLoading(false);
          return;
        }
        const currentKeyLocal = routeKey;
        // Track last fetched key but don't early-return to avoid missing state under StrictMode
        const alreadyFetched = lastFetchKeyRef.current === currentKeyLocal;
        if (alreadyFetched && item) { setLoading(false); return; }
        lastFetchKeyRef.current = currentKeyLocal;
        setLoading(true);
        let talent = null;
        if (creativeId) {
          const res = await apiGet(`/creatives/${creativeId}`);
          talent = res?.data || res; // controller returns single object
        } else if (slug) {
          const parts = String(slug).split('-');
          const last = parts[parts.length - 1] || '';
          const hasShort = /^[a-f0-9]{12}$/i.test(last);
          const looksHexish = /^[a-f0-9]{10,16}$/i.test(last);
          const baseStrict = hasShort ? parts.slice(0, -1).join('-') : slug;
          const baseRelaxed = looksHexish ? parts.slice(0, -1).join('-') : slug;
          const search = String(location?.search || '');
          const tokenParam = new URLSearchParams(search).get('token') || '';
          const tokenFromQuery = /^[a-f0-9]{12}$/i.test(tokenParam) ? tokenParam.toLowerCase() : '';
          const token = hasShort ? String(last).toLowerCase() : tokenFromQuery;
          // Resolve by searching talents (backend has no slug endpoint)
          try {
            // 1) Prefer server-side slug resolver using name base and optional token
            if (!talent) {
              try {
                const slugRes = await apiGet(`/creatives/slug/${encodeURIComponent(baseRelaxed)}`, token ? { token } : undefined);
                const slugTalent = slugRes?.data || slugRes;
                if (slugTalent?.id) {
                  talent = slugTalent;
                }
              } catch (e) { void e; }
              // 1b) As last resort, try canonical profile slug only if a valid token exists
              if (!talent && (hasShort || token)) {
                try {
                  const profSlug = token && !hasShort ? `${baseStrict}-${token}` : slug;
                  const profRes = await apiGet(`/creatives/profile/${encodeURIComponent(profSlug)}`);
                  const profTalent = profRes?.data || profRes;
                  if (profTalent?.id) {
                    talent = profTalent;
                  }
                } catch (e) { void e; }
              }
            }
            // 2) If still not found and a 12-char token is available, attempt resolving to full id via API
            if (!talent && token) {
              try {
                const resolveRes = await apiGet('/creatives/resolve', { short: token });
                const resolvedId = resolveRes?.data?.id || resolveRes?.id;
                if (resolvedId) {
                  const direct = await apiGet(`/creatives/${resolvedId}`);
                  const directTalent = direct?.data || direct;
                  if (directTalent?.id) {
                    talent = directTalent;
                  }
                }
              } catch (e) { void e; }
            }
            // 2b) If token is malformed length but hex-ish, try trimming to 12 from start/end
            if (!talent && !token && looksHexish) {
              const hex = String(last).toLowerCase().replace(/[^a-f0-9]/g, '');
              const candA = hex.slice(0, 12);
              const candB = hex.slice(-12);
              for (const c of [candA, candB]) {
                if (c && c.length === 12) {
                  try {
                    const resC = await apiGet('/creatives/resolve', { short: c });
                    const rid = resC?.data?.id || resC?.id;
                    if (rid) {
                      const direct = await apiGet(`/creatives/${rid}`);
                      const directTalent = direct?.data || direct;
                      if (directTalent?.id) { talent = directTalent; break; }
                    }
                  } catch (e) { void e; }
                }
              }
            }
            // 3) Client-side fallback: human-friendly search (hyphens -> spaces)
            if (!talent) {
              const searchTerm = String(baseRelaxed).replace(/[-_]+/g, ' ').trim();
              let listRes = await apiGet('/creatives', { search: searchTerm, limit: 500 });
              let talents = listRes?.data?.creatives || listRes?.data?.data?.creatives || listRes?.creatives || listRes || [];
              // Fallback: fetch without search if empty, then filter client-side by name/token
              if (!Array.isArray(talents) || talents.length === 0) {
                listRes = await apiGet('/creatives', { page: 1, limit: 500 });
                talents = listRes?.data?.creatives || listRes?.data?.data?.creatives || listRes?.creatives || listRes || [];
              }
              // Build comparison helpers
              const normalizedBase = slugify(baseRelaxed);
              const normalizedBaseTrimmed = String(normalizedBase).replace(/-[a-f0-9]+$/i, '');
              const nameSlugOf = (t) => {
                const display = String(t?.user?.display_name || '').trim();
                const first = String(t?.user?.first_name || '').trim();
                const lastn = String(t?.user?.last_name || '').trim();
                const composite = display || `${first} ${lastn}`.trim() || String(t?.name || '').trim();
                return slugify(composite);
              };
              const shortOf = (t) => {
                const rawId = t?.user?.id || t?.id || '';
                const idText = String(rawId).toLowerCase().replace(/[^0-9a-f]/g, '');
                return `${idText.slice(0, 6)}${idText.slice(-6)}`;
              };
              const shortAltOf = (t) => {
                const rawId = t?.user?.id || t?.id || '';
                const idText = String(rawId).toLowerCase().replace(/[^0-9a-f]/g, '');
                return `${idText.slice(0, 8)}${idText.slice(-4)}`;
              };
              // If name-only and multiple matches, surface chooser UI
              const nameMatches = (talents || []).filter((t) => {
                const ns = nameSlugOf(t);
                return ns === normalizedBase || ns === normalizedBaseTrimmed || ns.includes(normalizedBaseTrimmed) || normalizedBaseTrimmed.includes(ns);
              });
              if (!hasShort && nameMatches.length > 1) {
                setCandidates(nameMatches);
                talent = null;
              } else {
                if (!hasShort && nameMatches.length === 1) {
                  const only = nameMatches[0];
                  if (only?.id) {
                    try {
                      const res2 = await apiGet(`/creatives/${only.id}`);
                      talent = res2?.data || res2;
                    } catch {
                      talent = only;
                    }
                  } else {
                    talent = only || null;
                  }
                } else {
                  // 4) Find by name+token strictly, then name-only, then token-only
                let pick = (talents || []).find((t) => {
                  const ns = nameSlugOf(t);
                  const nameOk = ns === normalizedBase || ns === normalizedBaseTrimmed || ns.includes(normalizedBaseTrimmed) || normalizedBaseTrimmed.includes(ns);
                  const tokenOk = !hasShort || shortOf(t) === token || shortAltOf(t) === token;
                  return nameOk && tokenOk;
                })
                  || (talents || []).find((t) => {
                    const ns = nameSlugOf(t);
                    return ns === normalizedBase || ns === normalizedBaseTrimmed || ns.includes(normalizedBaseTrimmed) || normalizedBaseTrimmed.includes(ns);
                  })
                  || (hasShort ? (talents || []).find((t) => shortOf(t) === token || shortAltOf(t) === token) : null);

                  // 5) If still not found and we have a token, page through to find by token-only
                  if (!pick && hasShort) {
                    pick = null;
                  }

                  if (pick?.id) {
                    try {
                      const res2 = await apiGet(`/creatives/${pick.id}`);
                      talent = res2?.data || res2;
                  } catch {
                    talent = pick;
                  }
                  } else {
                    talent = pick || null;
                  }
                }
              }
            }
          } catch (e) {
            logger.warn('Talent slug resolution failed', e);
          }
        }
        if (talent) { setItem(talent); setCandidates([]); }
        // After fetching, redirect to canonical SEO path if needed (guarded)
        try {
          const maskedNameComposite = getPublicDisplayName(talent?.user) || '';
          const baseSlug = slugify(maskedNameComposite || '');
          const rawIdForCanonical = talent?.user?.id || talent?.id || '';
          const idText = String(rawIdForCanonical).toLowerCase().replace(/[^0-9a-f]/g, '');
          const shortCandidate = `${idText.slice(0, 8)}${idText.slice(-4)}`;
          const hasValidBase = !!baseSlug && /[a-z0-9]/.test(baseSlug);
          const isValidShort = shortCandidate.length === 12 && /^[a-f0-9]{12}$/.test(shortCandidate);
          if (hasValidBase && isValidShort) {
            const canonical = `${baseSlug}-${shortCandidate}`;
            const prefix = (location?.pathname || '').startsWith('/client/profile/') || (location?.pathname || '').startsWith('/clients/profile/')
              ? '/client/profile/'
              : '/creative/profile/';
            const canonicalPath = `${prefix}${canonical}`;
            if (!redirectedRef.current && location.pathname !== canonicalPath) {
              redirectedRef.current = true;
              navigate(canonicalPath, { replace: true });
            }
          }
        } catch (e) {
          logger.warn('Canonical redirect failed', e);
        }
      } catch (err) {
        logger.error('Failed to load talent detail', err);
        setError('Unable to load talent details');
      } finally {
        setLoading(false);
      }
    })();
  }, [creativeId, slug, routeKey, item, location.pathname, location.search, navigate]);

  // Fetch skills, services and categories related to this talent
  useEffect(() => {
    let mounted = true;
    const effectiveId = item?.id || creativeId;
    (async () => {
      if (!effectiveId) return;
      try {
        setLoadingExtra(true);
        setErrorExtra(null);
        const [csPayload, skillsPayload, servicesPayload, catsPayload] = await Promise.all([
          apiGet('/creative-skills', { creative_id: effectiveId, limit: 100 }),
          apiGet('/skills', { page: 1, limit: 500 }),
          apiGet('/services', { page: 1, limit: 500 }),
          apiGet('/service-categories', { page: 1, limit: 200 }),
        ]);

        const creativeSkills = csPayload?.data?.creative_skills || csPayload?.creative_skills || [];
        const skillList = skillsPayload?.data?.skills || skillsPayload?.skills || [];
        let servicesList = servicesPayload?.data?.services || servicesPayload?.services || [];
          const categoriesList = catsPayload?.data?.categories || catsPayload?.categories || [];

        // Map skill ids to names
        const skillMap = Object.fromEntries((skillList || []).map(s => [s.id, s.name]));
        const skillNames = (creativeSkills || [])
          .map(ts => skillMap[ts.skill_id])
          .filter(Boolean);

        // Services table uses creative_id column
        let myServices = (servicesList || []).filter(s => String(s.creative_id) === String(effectiveId));
        if (myServices.length === 0) {
          try {
            const pub = await apiGet('/services/public', { page: 1, limit: 500, verified_only: false });
            const pubList = pub?.data?.services || pub?.services || [];
            myServices = (pubList || []).filter(s => String(s.creative_id) === String(effectiveId));
          } catch (e) { void e; }
        }

        // Map categories from services
        const cmap = Object.fromEntries((categoriesList || []).map(c => [c.id, c.name]));

        if (mounted) {
          setSkills(skillNames);
          setServices(myServices);
          setCategoryMap(cmap);
        }
      } catch (e) {
        logger.error(e);
        if (mounted) setErrorExtra('Failed to load related data');
      } finally {
        if (mounted) setLoadingExtra(false);
      }
    })();
    return () => { mounted = false; };
  }, [item?.id, creativeId]);

  // Added: Fetch portfolio items and client reviews for this talent
  useEffect(() => {
    let mounted = true;
    const effectiveCreativeId = item?.id || creativeId;
    (async () => {
      if (!effectiveCreativeId) return;
      try {
        setLoadingPR(true);
        setErrorPR(null);
        const portfolioPayload = await apiGet('/portfolio-items', { creative_id: effectiveCreativeId, limit: 12 });
        const pItems = portfolioPayload?.data?.portfolio_items || portfolioPayload?.data?.data?.portfolio_items || portfolioPayload?.portfolio_items || [];
        if (mounted) {
          setPortfolioItems(Array.isArray(pItems) ? pItems : []);
        }
      } catch (e) {
        logger.error('Failed loading portfolio/reviews', e);
        if (mounted) setErrorPR('Failed to load portfolio or reviews');
      } finally {
        if (mounted) setLoadingPR(false);
      }
    })();
    return () => { mounted = false; };
  }, [item?.id, creativeId]);

  // SEO tags and canonical link (inside component)
  useEffect(() => {
    if (!item) return;
    try {
      const maskedNameCompositeItem = getPublicDisplayName(item?.user) || '';
      const baseSlug = slugify(maskedNameCompositeItem || '');
      const rawId = item?.user?.id || item?.id || '';
      const idText = String(rawId).toLowerCase().replace(/[^0-9a-f]/g, '');
      const short = `${idText.slice(0, 8)}${idText.slice(-4)}`;
      const prefix = (location?.pathname || '').startsWith('/client/profile/') || (location?.pathname || '').startsWith('/clients/profile/')
        ? '/client/profile/'
        : '/creative/profile/';
      const canonicalPath = `${prefix}${baseSlug}-${short}`;
      const origin = window.location.origin || '';
      const canonicalUrl = `${origin}${canonicalPath}`;

      // Canonical link tag
      let linkTag = document.head.querySelector('link[rel="canonical"]');
      if (!linkTag) {
        linkTag = document.createElement('link');
        linkTag.setAttribute('rel', 'canonical');
        document.head.appendChild(linkTag);
      }
      linkTag.setAttribute('href', canonicalUrl);

      // Helper to set meta tags
      const setMeta = (attr, key, val) => {
        if (!val) return;
        let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute(attr, key);
          document.head.appendChild(tag);
        }
        tag.setAttribute('content', String(val));
      };

      const desc = item?.bio || `${maskedNameCompositeItem} – Creative profile on LaLand`;
      const titleText = `${maskedNameCompositeItem}${item?.professional_title ? ` — ${item.professional_title}` : ''}`;
      document.title = `${titleText} | LaLand`;

      // Standard and Open Graph tags
      setMeta('name', 'description', desc);
      setMeta('property', 'og:title', titleText);
      setMeta('property', 'og:description', desc);
      setMeta('property', 'og:url', canonicalUrl);
const avatarUrl = item?.user?.avatar_url || item?.user?.avatar || '/logo-kreativ-light.png';
      setMeta('property', 'og:image', avatarUrl);

      // Robots: noindex disambiguation page
      if (Array.isArray(candidates) && candidates.length > 1) {
        setMeta('name', 'robots', 'noindex,follow');
      } else {
        setMeta('name', 'robots', 'index,follow');
      }
    } catch (e) {
      logger.warn('SEO tag update failed', e);
    }
  }, [item, candidates, location?.pathname]);

  

  if (loading) return (
  <div className="space-y-4" aria-busy="true">
    <div className="rounded-xl bg-secondary-bg border border-card-border p-4">
      <div className="flex items-center gap-3">
        <Skeleton variant="circle" width={40} height={40} />
        <div className="flex-1">
          <Skeleton variant="text" className="w-48 h-4" />
          <Skeleton variant="text" className="w-32 h-3 mt-2" />
        </div>
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <Skeleton variant="rect" className="h-44" />
      <Skeleton variant="rect" className="h-44" />
      <Skeleton variant="rect" className="h-44" />
    </div>
  </div>
);
  if (error) return <div className="text-sm text-red-400">{error}</div>;
  if (!item) {
    if (Array.isArray(candidates) && candidates.length > 1) {
      return (
        <div className="space-y-4">
          <div className="rounded-xl bg-secondary-bg border border-card-border p-4">
            <div className="text-sm text-neutral-300">Multiple creatives found</div>
            <div className="text-xs text-neutral-500 mt-1">Select the correct profile for this name.</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {candidates.map((t) => {
              const name = getPublicDisplayName(t?.user) || '';
              const baseSlug = slugify(name || '');
              const rawId = t?.user?.id || t?.id || '';
              const idText = String(rawId).toLowerCase().replace(/[^0-9a-f]/g, '');
              const short = `${idText.slice(0, 8)}${idText.slice(-4)}`;
              const prefix = (location?.pathname || '').startsWith('/client/profile/') || (location?.pathname || '').startsWith('/clients/profile/')
                ? '/client/profile/'
                : '/creative/profile/';
              const href = `${prefix}${baseSlug}-${short}`;
const avatar = t?.user?.avatar_url || t?.user?.avatar || '/logo-kreativ-light.png';
              return (
                <Link key={String(t?.id || rawId)} to={href} className="rounded-xl bg-secondary-bg border border-card-border p-3 flex items-center gap-3">
  <img src={avatar} alt={`${name} - Creative professional profile picture`} className="w-10 h-10 rounded-full object-cover border border-card-border" onError={(e)=>{e.currentTarget.src='/logo-kreativ-light.png';}} />
                  <div>
                    <div className="text-sm font-medium text-primary-text">{name}</div>
                    <div className="text-xs text-secondary-text">View profile</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      );
    }
    return <div className="text-sm text-neutral-400">Not found</div>;
  }

  // Computed UI fields
const avatarUrl = item?.user?.avatar_url || item?.user?.avatar || '/logo-kreativ-light.png';
  const displayName = getPublicDisplayName(item?.user) || 'Creative Profile';
  const title = item?.professional_title || item?.user?.title || '';
  const city = item?.city_name || item?.location?.city || item?.location?.name || '';
  const country = countryNameFromCode(item?.user?.country_code || '');
  const rating = item?.rating ?? 0;
  const reviews = item?.total_reviews ?? 0;
  const rate = item?.hourly_rate ?? 'N/A';
  const currency = item?.currency || 'SEK';
  const isVerified = Boolean(item?.verification_badge) || ['ai_verified','admin_approved'].includes(String(item?.verification_status || ''));

  // New: header stats derived labels
  const createdAtRaw = item?.user?.created_at || item?.created_at || item?.user?.createdAt || item?.createdAt;
  const memberSinceLabel = createdAtRaw ? new Date(createdAtRaw).toLocaleDateString('en-US') : '—';
  

  // SEO moved above early returns to preserve hook order

  return (
    <div className="space-y-6 mb-12">
      {/* Header card */}
      <div className="rounded-xl bg-secondary-bg border border-card-border p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 rounded-full overflow-hidden border border-card-border bg-neutral-900 flex items-center justify-center">
              {avatarUrl ? (
                <img src={avatarUrl} alt={`${displayName} - ${title || 'Creative professional'} profile photo`} className="w-full h-full object-cover" />
              ) : (
                <span className="text-lg font-semibold text-neutral-300">{initialsOf(displayName)}</span>
              )}
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-primary-text">{displayName}</h1>
              {title && <div className="text-sm md:text-base text-secondary-text mt-1">{title}</div>}
              <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-secondary-text">
                {(city || country) && (
                  <span className="inline-flex items-center gap-1"><MapPin size={16} />{city}{city && country ? ', ' : ''}{country}</span>
                )}
                <span className="inline-flex items-center gap-1"><Star size={16} className="text-yellow-500" />{rating}</span>
                {isVerified && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border border-card-border bg-secondary-bg text-secondary-text"><ShieldCheck size={14} className="text-emerald-600 dark:text-emerald-400" />Verified</span>
                )}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs md:text-sm text-secondary-text">
                <span className="inline-flex items-center gap-1">
                  <Calendar size={16} /> Member Since {memberSinceLabel}
                </span>
                <span className="inline-flex items-center gap-1">
                  {/* Projects Completed hidden */}
                </span>
                <span className="inline-flex items-center gap-1">
                  {/* On-Time Delivery hidden */}
                </span>
              </div>
            </div>
          </div>
          <div className="min-w-[220px]">
            <div className="text-lg font-semibold text-primary-text inline-flex items-center gap-2"><BadgeDollarSign size={18} />{rate !== 'N/A' ? formatPrice(convert(rate, currency, userCurrency), userCurrency) + '/HR' : `${rate}`}</div>
            <div className="mt-3 flex items-center gap-2">
              <button type="button" onClick={() => {
                setShowBooking((v) => !v);
                setTimeout(() => {
                  if (bookingRef.current && window.innerWidth < 768) {
                    bookingRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100);
              }} className="btn btn-primary btn-sm">Book Now</button>
              <ContactCreativeButton creativeUserId={item?.user?.id || item?.id} label="Message" icon={false} className="btn btn-primary btn-sm inline-flex" />
            </div>
            <div className="mt-3 flex items-center gap-3">
              {/* 
              //Disabling Like and Share UI - Start
              <button type="button" onClick={toggleLike} aria-pressed={liked} aria-disabled={hasLikedOnce && !liked} disabled={hasLikedOnce && !liked} title={liked ? 'Unlike' : (hasLikedOnce ? 'You already liked this' : 'Like')} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-card-border bg-secondary-bg text-primary-text hover-menu disabled:opacity-60 disabled:cursor-not-allowed">
                <Heart size={18} className={liked ? 'text-red-500' : 'text-secondary-text'} fill={liked ? 'currentColor' : 'none'} />
                <span className="text-xs">{likesCount}</span>
              </button>
              <div className="relative">                
                <button type="button" onClick={() => setShareOpen((v)=>!v)} aria-expanded={shareOpen} aria-controls="share-menu" className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-card-border bg-secondary-bg text-primary-text hover:bg-white/10">
                  <Share2 size={18} />
                  <span className="text-xs">Share</span>
                </button>
                {shareOpen && (
                  <div id="share-menu" className="absolute right-0 z-10 mt-2 w-48 rounded-md border border-card-border bg-secondary-bg shadow-lg p-2 text-sm text-primary-text">
                    <button type="button" onClick={tryNativeShare} className="w-full text-left px-2 py-1 rounded hover-menu">Share via device…</button>
                    <button type="button" onClick={copyLink} className="w-full text-left px-2 py-1 rounded hover-menu">{copied ? 'Link copied!' : 'Copy link'}</button>
                    <div className="my-1 h-px bg-card-border" />
                    <button type="button" onClick={() => openShare('twitter')} className="w-full text-left px-2 py-1 rounded hover-menu">Share on Twitter/X</button>
                    <button type="button" onClick={() => openShare('linkedin')} className="w-full text-left px-2 py-1 rounded hover-menu">Share on LinkedIn</button>
                    <button type="button" onClick={() => openShare('facebook')} className="w-full text-left px-2 py-1 rounded hover-menu">Share on Facebook</button>
                    <button type="button" onClick={() => openShare('whatsapp')} className="w-full text-left px-2 py-1 rounded hover-menu">Share on WhatsApp</button>
                  </div>
                )}                
              </div>
              //Disabling Like and Share UI - End
              */}
            </div>
          </div>
        </div>
      </div>

      {/* Content grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 space-y-4">
          <div className="rounded-xl bg-secondary-bg border border-card-border p-4">
            <div className="text-base font-semibold text-primary-text">About</div>
            <p className="mt-2 text-secondary-text whitespace-pre-line">{item?.user?.bio?.trim() || item?.bio?.trim() || item?.meta_description?.trim() || 'No bio available.'}</p>
          </div>

          <div className="rounded-xl bg-secondary-bg border border-card-border p-4">
            <div className="flex items-center justify-between">
              <div className="text-base font-semibold text-primary-text">Skills</div>
              {loadingExtra && <div className="text-xs text-secondary-text">Loading…</div>}
            </div>
            {errorExtra && <div className="text-xs text-red-400 mt-1">{errorExtra}</div>}
            <div className="mt-2 flex flex-wrap gap-2">
              {(skills && skills.length > 0) ? (
                skills.map((name) => (
                  <span key={name} className="inline-flex items-center text-xs px-2 py-1 rounded-md bg-secondary-bg text-secondary-text border border-card-border">{name}</span>
                ))
              ) : (
                <div className="text-xs text-neutral-500">No skills listed.</div>
              )}
            </div>
          </div>

          {/* Services section moved below Portfolio */}

          {/* Added: Portfolio section */}
          <div className="rounded-xl bg-secondary-bg border border-card-border p-4">
            <div className="flex items-center justify-between">
              <div className="text-base font-semibold text-primary-text">Portfolio</div>
              {loadingPR && <div className="text-xs text-neutral-500">Loading…</div>}
            </div>
            {errorPR && <div className="text-xs text-red-400 mt-1">{errorPR}</div>}
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {(portfolioItems || []).length > 0 ? (
                (portfolioItems || []).map((p, i) => {
                  const img = p.image_url || p.image || p.cover_image || '';
                  const src = normalizeImageUrl(img);
                  return (
                    <div key={p.id} className="rounded-lg overflow-hidden border border-card-border bg-card-bg card-hover transition-transform duration-200 hover:shadow-lg hover:-translate-y-[1px]">
                      <button type="button" className="block w-full cursor-pointer" onClick={() => openLightboxAt(i)}>
                        <img src={src || '/vite.svg'} alt={p.title || 'Project'} className="w-full h-40 object-cover" loading="lazy" onError={(e) => { e.currentTarget.src = '/vite.svg'; }} />
                      </button>
                      <div className="p-3">
                        <div className="text-sm font-medium text-primary-text">{p.title || 'Untitled project'}</div>
                        {p.description && (
                          <div className="mt-1 text-xs text-secondary-text line-clamp-3">{p.description}</div>
                        )}
                        {p.project_url && (
                          <a href={p.project_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-xs text-blue-400 hover:underline">Visit project</a>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-xs text-neutral-500">No portfolio items.</div>
              )}
            </div>
          </div>

          {/* Reviews section hidden */}

          <div className="rounded-xl bg-secondary-bg border border-card-border p-4 lg:p-6">
            <div className="text-base font-semibold text-primary-text">Services</div>
            <div className="mt-4">
              {(services || []).length > 0 ? (
                <section className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5 xl:gap-8 2xl:gap-10">
                    {services.map((s) => {
                      // Derive primary image similar to AllServices
                      const imagesField = s.images || s.images_json || s.imagesList || s.images_list || s.gallery || [];
                      const imagesCandidateList = (() => {
                        if (Array.isArray(imagesField)) return imagesField.filter(Boolean);
                        if (typeof imagesField === 'string') {
                          try {
                            const parsed = JSON.parse(imagesField);
                            return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
                          } catch (e) { void e; return []; }
                        }
                        if (typeof imagesField === 'object' && imagesField) {
                          const vals = Object.values(imagesField).filter(Boolean);
                          return Array.isArray(vals) ? vals : [];
                        }
                        return [];
                      })();
                      const imageCandidates = imagesCandidateList
                        .map((it) => typeof it === 'string' ? it : (it?.url || it?.src || it?.image || it?.image_url))
                        .filter(Boolean);
                      const sid = String(s?.id || '');
                      const imgErrored = Boolean(sid && imgErrorById[sid]);
                      const rawPrimary = (
                        s.image || s.image_url || s.imageUrl ||
                        s.cover_image || s.cover_image_url || s.coverImage || s.coverImageUrl ||
                        s.header_image || s.headerImage ||
                        s.featured_image || s.featuredImage ||
                        imageCandidates[0] || ''
                      );
                      const normalizedPrimary = normalizeImageUrl(rawPrimary);
                      const hasPrimary = Boolean(normalizedPrimary) && !imgErrored;
                      const imageSrc = hasPrimary ? normalizedPrimary : '/placeholder.jpg';
                      const hasImage = hasPrimary;
                      

                      // Category name
                      const categoryName = (s?.category_id && categoryMap?.[s.category_id]) || s?.category || s?.subcategory || s?.mainCategory;

                      

                      // Price label similar to AllServices
                      // Use service fixed price if available; otherwise fallback to talent hourly (/HR)
                      const servicePrice = s?.package_price ?? s?.base_price ?? s?.price ?? null;
                      const serviceCurrency = String(s?.currency || currency || 'SEK').toUpperCase();
                      const hasTalentHourly = item?.hourly_rate != null && item?.hourly_rate !== '' && !Number.isNaN(Number(item?.hourly_rate));
                      // Convert prices to user's selected currency
                      const convertedServicePrice = servicePrice != null ? convert(servicePrice, serviceCurrency, userCurrency) : null;
                      const convertedHourlyRate = hasTalentHourly ? convert(item?.hourly_rate, currency, userCurrency) : null;
                      const priceLabel = convertedServicePrice != null
                        ? formatPrice(convertedServicePrice, userCurrency)
                        : (convertedHourlyRate != null
                          ? `${formatPrice(convertedHourlyRate, userCurrency)}/HR`
                          : '—');

                      // Link path like AllServices
                      const titleSlug = slugify(s.title || '');
                      const baseSlug = titleSlug || (categoryName ? slugify(categoryName) : '');
                      const talentInitials = initialsOf(displayName);
                      const idText = String(s.id || '').toLowerCase().replace(/[^0-9a-f-]/g, '');
                      const idFirst = idText.replace(/-/g, '').slice(0, 6);
                      const idLast = idText.replace(/-/g, '').slice(-6);
                      const idToken = `${idFirst}${idLast}`;
                      const toPath = baseSlug
                        ? `/services/${baseSlug}${talentInitials ? `-${talentInitials}` : ''}-${idToken}`
                        : `/services/${idToken}`;

                      return (
                        <div key={s.id} className="bg-card-bg card-hover hover-lift border-gradient rounded-xl shadow-md border border-card-border h-full flex flex-col overflow-hidden">
                          <div className="relative">
                            {hasImage ? (
                              <img
                                src={imageSrc}
                                alt={s.title || s.name || 'Service'}
                                loading="lazy"
                                onError={() => { const key = String(s?.id || ''); if (key) setImgErrorById((prev) => ({ ...prev, [key]: true })); }}
                                className="w-full h-44 sm:h-48 lg:h-48 object-cover border-b border-card-border"
                              />
                            ) : (
                              <div className="w-full h-44 sm:h-48 lg:h-48 border-b border-card-border bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-700" aria-hidden="true" />
                            )}
                            {hasImage && (
                              <div className="image-overlay overlay-gradient-bottom" aria-hidden="true" />
                            )}
                            
                            <div className="absolute bottom-0 left-0 right-0 px-4 py-3 text-white">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="font-semibold text-sm md:text-base line-clamp-2 drop-shadow-sm">
                                    {s.title || s.name || 'Untitled Service'}
                                  </div>
                                  {categoryName && (
                                    <div className="mt-0.5 text-[11px] inline-flex items-center gap-1 drop-shadow-sm">
                                      <span className="px-2 py-0.5 rounded-full border border-white/30 bg-black/30 inline-flex items-center gap-1">
                                        <Tag size={12} />
                                        <span>{categoryName}</span>
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="mt-1 flex items-center justify-between text-[12px]">
                                <div className="inline-flex items-center gap-3">
                                  <span className="inline-flex items-center gap-1">
                                    <BadgeDollarSign size={14} className="icon-float" />
                                    {priceLabel}
                                  </span>
                                  <span className="inline-flex items-center gap-1">
                                    <Star size={14} className="text-yellow-400 icon-float" />
                                    {Number(rating).toFixed(1)} ({reviews})
                                  </span>
                                </div>
                                {s.id ? (
                                  <Link to={toPath} className="text-xs bg-white/20 hover:bg-white/30 text-white rounded px-2 py-1 transition-colors">
                                    View Detail
                                  </Link>
                                ) : (
                                  <span className="text-xs text-white/70">View</span>
                                )}
                              </div>
                            </div>
                          </div>

                        </div>
                      );
                    })}
                  </div>
                </section>
              ) : (
                <div className="text-xs text-neutral-500">No services listed.</div>
              )
            }
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {/* Categories section hidden */
          }

          <div className="rounded-xl bg-secondary-bg border border-card-border p-4">
            <div className="text-base font-semibold text-primary-text">Performance</div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-secondary-text">Rating:</span> <span className="text-primary-text font-medium">{Number(rating).toFixed(1)}</span></div>
              <div><span className="text-secondary-text">Reviews:</span> <span className="text-primary-text font-medium">{reviews}</span></div>
              {/* Completed hidden */}
              {/* Response Time hidden */}
            </div>
          </div>

          {showBooking && (
            <div ref={bookingRef} className="rounded-xl bg-secondary-bg border border-card-border p-4">
              <QuickFikaBooking inline creativeId={creativeId || item?.id} />
            </div>
          )}
        </div>

        {/* Portfolio Lightbox (dashboard variant) */}
        {lbOpen && (
          <Lightbox open={lbOpen} close={() => setLbOpen(false)} slides={lbSlides} index={lbIndex} />
        )}

      </div>
    </div>
  );
}

// Helpers: normalize image URLs and category icon
function normalizeImageUrl(src) {
  if (!src) return '';
  let s = String(src).trim();
  s = s.replace(/^[\s"'`]+|[\s"'`]+$/g, '');
  
  // Data URLs - return as-is
  if (s.startsWith('data:')) return s;
  
  // Already absolute http(s) URL
  if (/^https?:/i.test(s)) {
    // Check if it's an uploads URL - convert to relative path for Vite proxy
    try {
      const parsed = new URL(s);
      if (parsed.pathname.startsWith('/uploads/') || parsed.pathname.startsWith('/uploads')) {
        // Return relative path to use Vite proxy (avoids CORS)
        return parsed.pathname;
      }
    } catch { /* invalid URL, return as-is */ }
    return s;
  }
  
  // Protocol-relative URL
  if (s.startsWith('//')) return `${window?.location?.protocol || 'https:'}${s}`;
  
  // Already relative path - return as-is
  if (s.startsWith('/')) return s;
  // Relative path without leading slash
  return `/${s}`;
}
function CategoryIcon({ iconKey, size = 12 }) {
  const DefaultComp = useIcon('md:MdCategory');
  const IconComp = useIcon(iconKey || 'md:MdCategory');
  if (IconComp) return <IconComp size={size} />;
  if (DefaultComp) return <DefaultComp size={size} />;
  return null;
}

function countryNameFromCode(code) {
  const MAP = {
    SE: 'Sweden', NO: 'Norway', DK: 'Denmark', FI: 'Finland', IS: 'Iceland',
    DE: 'Germany', NL: 'Netherlands', BE: 'Belgium', FR: 'France', ES: 'Spain',
    IT: 'Italy', PT: 'Portugal', GB: 'United Kingdom', IE: 'Ireland', US: 'United States',
    CA: 'Canada', AU: 'Australia', NZ: 'New Zealand', CH: 'Switzerland', AT: 'Austria',
    PL: 'Poland', CZ: 'Czechia', SK: 'Slovakia', HU: 'Hungary', EE: 'Estonia', LT: 'Lithuania', LV: 'Latvia'
  };
  const raw = String(code || '').trim().toUpperCase();
  return MAP[raw] || (code || '').trim();
}
